import { useNavigate } from "react-router-dom";
import { RoutePath } from "@/routes";
import type { CreateABTestLocationState } from "@/pages/CreateABTest";
import { TabHeader } from "@/components/shared/TabHeader";
import { useAppData } from "@/contexts/AppDataContext";
import { productApi } from "@/resources/product";
import { StrategyBrowserStates } from "./StrategyBrowserStates";
import type { SeoStrategyRecommendation } from "@/types/seo-strategy";

interface SeoStrategyTabProps {
  asin: string;
  productId: number;
  brandName: string;
}

/**
 * AUT-223 Section 5 / AUT-430 / WEB-001: the Strategy tab. `StrategyBrowserStates` owns
 * the tab-level state machine and renders `StrategyBrowser` (`WEB-006`, `WEB-009`) once it
 * has decided the tab is Loaded -- `components/seo-strategy.md § The screen`. This
 * component keeps only the heading, the A/B test launch context (`startTest`) and the
 * hero-product read it needs, per
 * `decisions/the-strategy-browser-takes-its-ab-creation-action-and-segment-as-props.md`
 * decision 3.
 */
export function SeoStrategyTab({ asin, productId, brandName }: SeoStrategyTabProps) {
  const navigate = useNavigate();
  const { selectedBrandId } = useAppData();

  // The A/B test launch context needs the full hero-product record, the same one the
  // Content Scorecard tab hands over when it starts a test from a gap.
  const { data: heroAsinData } = productApi.useGetHeroProductABTests(
    String(productId),
    selectedBrandId ?? undefined,
  );

  // Section 6: hands off to A/B test creation with the recommendation's context
  // pre-filled, carrying its id so the created test links back by id. "Start first test"
  // is a GAP test, and the card is a CONSOLIDATION of several COSMO questions -- so every
  // one of its source gaps is applied. The per-keyword row hand-off is gone with
  // `RecommendedTestsTable` (AUT-430): `StrategyOpportunitiesList` has no row buttons.
  const startTest = (recommendation: SeoStrategyRecommendation) => {
    if (!heroAsinData) return;

    const state: CreateABTestLocationState = {
      heroAsin: heroAsinData,
      productId,
      initialTargetPhrase: recommendation.thematicGrouping?.keywords[0]?.keyword,
      prefillFromScorecard: {
        question: recommendation.gapTitle ?? "",
        recommendation: recommendation.hypothesis ?? "",
        evidence: recommendation.evidenceSummary ?? "",
        category: recommendation.category ?? "",
        finding: recommendation.whyItMatters ?? "",
        // Every COSMO question this card consolidated. The A/B test page already loads
        // the product's scorecard, so it applies these from its own data rather than us
        // shipping half-built gap objects across the navigation.
        relationKeys: recommendation.sourceRelationKeys ?? [],
        // FR-8a: the strategy's own Field selects the test's content fields, not an
        // inference over the hypothesis text -- omitted when the strategy has none.
        ...(recommendation.field ? { field: recommendation.field } : {}),
      },
      defaultSource: "scorecard",
      // The link back to the recommendation is what makes a linked test read a real
      // status instead of being matched by text.
      seoStrategyRecommendationId: recommendation.id,
      returnTo: window.location.pathname + window.location.search,
    };
    navigate(RoutePath.CreateABTest, { state });
  };

  return (
    <div className="space-y-6 p-6">
      <TabHeader title="Strategy" />
      <StrategyBrowserStates
        asin={asin}
        productId={productId}
        brandName={brandName}
        onStartTest={heroAsinData ? startTest : undefined}
        // AUT-196 (design QA 2026-10-14): the no-data copy tells the user to generate an Amazon
        // AI score; this takes them to the tab that does it. The run itself still starts from
        // that tab's own button -- nothing here triggers a paid job.
        onGenerateScore={() => navigate("../content-scorecard")}
      />
    </div>
  );
}
